interface PricingProps {
  onReserveClick: () => void;
}

export default function Pricing({ onReserveClick }: PricingProps) {
  const packages = [
    {
      name: 'Apartment Move',
      totes: '12 totes',
      rental: '3 day rental',
      price: '$125',
      description: 'Best for studio or 1 bedroom moves.',
      popular: false
    },
    {
      name: 'Standard Home Move',
      totes: '20 totes',
      rental: '3 day rental',
      price: '$165',
      description: 'Perfect for most 2–3 bedroom homes.',
      popular: true
    },
    {
      name: 'Large Home Move',
      totes: '32 totes',
      rental: '3 day rental',
      price: '$224',
      description: 'Ideal for large homes and families.',
      popular: false
    },
    {
      name: 'Estate Move',
      totes: '50 totes',
      rental: '3 day rental',
      price: '$300',
      description: 'Best for very large homes or downsizing.',
      popular: false
    }
  ];

  const addons = [
    {
      name: 'Extra day rental',
      price: '$20/day',
      image: 'https://public.readdy.ai/ai/img_res/e3daebdad3c1e6fc1acd4adc4e4fee2f.jpg'
    },
    {
      name: 'Extra totes',
      price: '$6 each',
      image: 'https://readdy.ai/api/search-image?query=single%20bright%20yellow%20plastic%20moving%20tote%20box%20with%20lid%20on%20white%20background%2C%20product%20photography%2C%20clean%20minimal%20style%2C%20professional%20studio%20lighting&width=80&height=80&seq=addon2&orientation=squarish'
    },
    {
      name: '2-Wheel Hand Truck',
      price: '$20',
      image: 'https://readdy.ai/api/search-image?query=hand%20truck%20dolly%20moving%20equipment%20on%20white%20background%2C%20product%20photography%2C%20clean%20minimal%20style%2C%20professional%20studio%20lighting%2C%20metal%20frame&width=80&height=80&seq=addon3&orientation=squarish'
    },
    {
      name: 'Label and marker kit',
      price: '$10',
      image: 'https://readdy.ai/api/search-image?query=moving%20label%20stickers%20and%20black%20marker%20pen%20kit%20on%20white%20background%2C%20product%20photography%2C%20clean%20minimal%20style%2C%20professional%20studio%20lighting%2C%20organized%20flat%20lay&width=80&height=80&seq=addon5&orientation=squarish'
    },
    {
      name: 'Moving Blankets (8 pack)',
      price: '$20',
      image: 'https://readdy.ai/api/search-image?query=folded%20moving%20blankets%20furniture%20pads%20stack%20on%20clean%20white%20background%2C%20product%20photography%2C%20minimalist%20style%2C%20professional%20studio%20lighting%2C%20soft%20gray%20textile&width=80&height=80&seq=addon6&orientation=squarish'
    },
    {
      name: 'Moving Blankets (12 pack)',
      price: '$30',
      image: 'https://readdy.ai/api/search-image?query=large%20stack%20of%20folded%20moving%20blankets%20furniture%20protection%20pads%20on%20white%20background%2C%20product%20photography%2C%20clean%20minimal%20style%2C%20professional%20studio%20lighting%2C%20thick%20padded%20blankets&width=80&height=80&seq=addon7&orientation=squarish'
    },
    {
      name: '4-Wheel Dolly',
      price: '$10/pc',
      image: 'https://readdy.ai/api/search-image?query=four%20wheel%20platform%20dolly%20cart%20moving%20equipment%20on%20white%20background%2C%20product%20photography%2C%20clean%20minimal%20style%2C%20professional%20studio%20lighting%2C%20flat%20furniture%20mover%20with%20wheels&width=80&height=80&seq=addon8&orientation=squarish'
    },
    {
      name: 'Bubble Wrap - 12" x 250\'',
      price: '$35/roll',
      image: 'https://readdy.ai/api/search-image?query=roll%20of%20bubble%20wrap%20packing%20material%20on%20white%20background%2C%20product%20photography%2C%20clean%20minimal%20style%2C%20professional%20studio%20lighting%2C%20transparent%20protective%20packaging&width=80&height=80&seq=addon9&orientation=squarish'
    }
  ];

  return (
    <section id="pricing" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Pricing Packages</h2>
          <p className="text-lg text-gray-600">
            Choose the perfect package for your move
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {packages.map((pkg, index) => (
            <div key={index} className={`relative bg-white rounded-2xl p-8 ${pkg.popular ? 'ring-2 ring-yellow-400 shadow-xl' : 'shadow-md'} hover:shadow-xl transition-shadow`}>
              {pkg.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-yellow-400 text-gray-900 px-4 py-1 rounded-full text-sm font-bold whitespace-nowrap">
                    Most Popular
                  </span>
                </div>
              )}
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                <div className="text-5xl font-bold text-gray-900 mb-2">{pkg.price}</div>
                <div className="text-gray-600 text-sm mb-1">{pkg.totes}</div>
                <div className="text-gray-600 text-sm">{pkg.rental}</div>
              </div>
              <p className="text-gray-600 text-center mb-6">{pkg.description}</p>
              <button onClick={onReserveClick} className={`w-full py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer ${pkg.popular ? 'bg-yellow-400 text-gray-900 hover:bg-yellow-500' : 'bg-gray-900 text-white hover:bg-gray-800'}`}>
                Select Package
              </button>
            </div>
          ))}
        </div>

        <div className="text-center mb-12">
          <p className="text-gray-600 font-medium">
            Delivery and pickup included.
          </p>
        </div>

        <div className="max-w-4xl mx-auto bg-white rounded-2xl p-8 shadow-md">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Optional Add-Ons</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {addons.map((addon, index) => (
              <div key={index} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="w-14 h-14 flex-shrink-0 rounded-lg overflow-hidden bg-white border border-gray-100">
                  <img
                    src={addon.image}
                    alt={addon.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <span className="text-gray-900 font-medium flex-1">{addon.name}</span>
                <span className="text-gray-900 font-bold whitespace-nowrap">{addon.price}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
